import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Settings.css';

const DENOMINATIONS = [
  'Catholic', 'Protestant', 'Pentecostal', 'Orthodox', 'Non-denominational',
  'Anglican', 'Baptist', 'Methodist', 'Adventist', 'Evangelical', 'Other',
];

const GENDER_OPTIONS = [
  { value: 'female', label: 'Women' },
  { value: 'male', label: 'Men' },
  { value: 'everyone', label: 'Everyone' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    API.get('/api/preferences')
      .then((res) => {
        if (!cancelled) setPrefs(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePref = async (patch) => {
    if (!prefs) return;
    setPrefs({ ...prefs, ...patch });
    setSaving(true);
    try {
      await API.put('/api/preferences', patch);
      sessionStorage.removeItem('swipeCandidates');
      sessionStorage.removeItem('swipeIndex');
    } catch (err) {
      // Re-fetch on error to revert
      const fresh = await API.get('/api/preferences');
      setPrefs(fresh.data);
    } finally {
      setSaving(false);
    }
  };

  const toggleDenomination = (denom) => {
    const current = prefs.denomination_preferences || [];
    const next = current.includes(denom)
      ? current.filter((d) => d !== denom)
      : [...current, denom];
    updatePref({ denomination_preferences: next });
  };

  const [locationEnabled, setLocationEnabled] = useState(
    localStorage.getItem('locationEnabled') !== 'false'
  );

const handleLocationToggle = () => {
    const next = !locationEnabled;
    setLocationEnabled(next);
    localStorage.setItem('locationEnabled', String(next));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading || !prefs) {
    return <div className="settings-page"><div className="settings-loading">Loading...</div></div>;
  }

  return (
    <div className="settings-page">
      <header className="settings-header">
        <h1>Settings</h1>
        {saving && <span className="saving-indicator">Saving...</span>}
      </header>
      
    <section className="settings-section">
  <button
    className="settings-row link premium-link"
    onClick={() => navigate('/premium')}
  >
    <span>✨ Upgrade to Godly+</span>
    <span className="row-arrow">›</span>
  </button>
  <button
    className="settings-row link donate-link"
    onClick={() => navigate('/donate')}
  >
    <span>❤️ Donate to Godly Match</span>
    <span className="row-arrow">›</span>
  </button>
    </section>

      {/* ===== Profile ===== */}
      <section className="settings-section">
        <button
          className="settings-row link"
          onClick={() => navigate('/profile/edit')}
        >
          <span>Edit my profile</span>
          <span className="row-arrow">›</span>
        </button>
      </section>

      {/* ===== Account ===== */}
      <section className="settings-section">
        <h2>Account</h2>

        <div className="settings-row">
          <span className="row-label">Email</span>
          <span className="row-value">{user?.email}</span>
        </div>

        <button
          className="settings-row link"
          onClick={() => setShowChangePassword(true)}
        >
          <span>Change password</span>
          <span className="row-arrow">›</span>
        </button>

        <button className="settings-row link" onClick={handleLogout}>
          <span>Log out</span>
          <span className="row-arrow">›</span>
        </button>

        <button
          className="settings-row link danger"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <span>Delete my account</span>
          <span className="row-arrow">›</span>
        </button>
      </section>

      {/* ===== Preferences ===== */}
      <section className="settings-section">
        <h2>Preferences</h2>

        <div className="settings-row stacked">
          <div className="row-header">
            <span className="row-label">Age range</span>
            <span className="row-value">
              {prefs.age_min} – {prefs.age_max}
            </span>
          </div>
          <div className="dual-slider">
            <input
              type="range"
              min={18}
              max={99}
              value={prefs.age_min}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (v <= prefs.age_max) updatePref({ age_min: v });
              }}
            />
            <input
              type="range"
              min={18}
              max={99}
              value={prefs.age_max}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (v >= prefs.age_min) updatePref({ age_max: v });
              }}
            />
          </div>
        </div>

        <div className="settings-row stacked">
          <div className="row-header">
            <span className="row-label">Maximum distance</span>
            <span className="row-value">
              {prefs.max_distance_km
                ? `${prefs.max_distance_km} km`
                : 'Anywhere'}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={3000}
            step={10}
            value={prefs.max_distance_km || 0}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              updatePref({ max_distance_km: v === 0 ? -1 : v });
            }}
          />
        </div>

        <div className="settings-row stacked">
          <span className="row-label">Show me</span>
          <div className="chip-row">
            {GENDER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`chip ${prefs.gender_preference === opt.value ? 'selected' : ''}`}
                onClick={() => updatePref({ gender_preference: opt.value })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="settings-row stacked">
          <span className="row-label">Denominations</span>
          <p className="row-hint">
            Leave all unselected to see everyone.
          </p>
          <div className="chip-row wrap">
            {DENOMINATIONS.map((d) => (
              <button
                key={d}
                className={`chip ${(prefs.denomination_preferences || []).includes(d) ? 'selected' : ''}`}
                onClick={() => toggleDenomination(d)}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="settings-row stacked">
          <div className="row-header">
            <span className="row-label">Minimum faith level</span>
            <span className="row-value">{prefs.min_faith_level}</span>
          </div>
          <input
            type="range"
            min={1}
            max={5}
            value={prefs.min_faith_level}
            onChange={(e) => updatePref({ min_faith_level: parseInt(e.target.value) })}
          />
        </div>
      </section>

      {/* ===== Notifications ===== */}
            {/* ===== Notifications ===== */}
      <section className="settings-section">
        <h2>Notifications</h2>

        <ToggleRow
          label="Email notifications"
          subtitle="Master switch for all notification emails below"
          checked={prefs.email_notifications_enabled}
          onChange={(v) => updatePref({ email_notifications_enabled: v })}
        />
        <ToggleRow
          label="New matches"
          checked={prefs.notify_new_matches}
          onChange={(v) => updatePref({ notify_new_matches: v })}
        />
        <ToggleRow
          label="New messages"
          checked={prefs.notify_new_messages}
          onChange={(v) => updatePref({ notify_new_messages: v })}
        />
        <ToggleRow
          label="Profile likes"
          subtitle="Godly+ feature"
          checked={prefs.notify_profile_likes}
          onChange={(v) => updatePref({ notify_profile_likes: v })}
        />
      </section>

            {/* ===== Contat Support ===== */}
      <section className="settings-section">
    <h2>Support</h2>
    <a
        href="mailto:support@godlymatch.org?subject=Support Request"
        className="settings-support-link"
    >
        <span>Contact Support</span>
        <span>→</span>
    </a>
</section>

      {/* ===== Privacy ===== */}
      <section className="settings-section">
        <h2>Privacy</h2>

        <ToggleRow
          label="Show my distance"
          subtitle="Others can see how far away you are"
          checked={prefs.show_distance}
          onChange={(v) => updatePref({ show_distance: v })}
        />
        <ToggleRow
          label="Show my age"
          subtitle="Others can see your age on your profile"
          checked={prefs.show_age}
          onChange={(v) => updatePref({ show_age: v })}
        />
            <ToggleRow
        label="Location services"
        subtitle="Used to show you nearby profiles"
        checked={locationEnabled}
        onChange={handleLocationToggle}
      />
        </section>
        
      <section className="settings-section">
        <h2>Legal</h2>
        <a href="/privacy" className="settings-row link" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>Privacy Policy</span>
          <span className="row-arrow">›</span>
        </a>
        <a href="/terms" className="settings-row link" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>Terms of Service</span>
          <span className="row-arrow">›</span>
        </a>
        <a href="/community-guidelines" className="settings-row link" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>Community Guidelines</span>
          <span className="row-arrow">›</span>
        </a>
      </section>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {showDeleteConfirm && (
        <DeleteAccountModal
          onClose={() => setShowDeleteConfirm(false)}
          onSuccess={() => {
            logout();
            navigate('/login');
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Toggle row
// =====================================================================
function ToggleRow({ label, subtitle, checked, onChange }) {
  return (
    <div className="settings-row toggle">
      <div className="toggle-text">
        <span className="row-label">{label}</span>
        {subtitle && <span className="row-hint">{subtitle}</span>}
      </div>
      <label className="toggle-switch">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="toggle-slider" />
      </label>
    </div>
  );
}

// =====================================================================
// Change password modal
// =====================================================================
function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (newPwd.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPwd !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await API.put('/api/auth/change-password', {
        current_password: current,
        new_password: newPwd,
      });
      alert('Password changed');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2>Change password</h2>
        <input
          type="password"
          placeholder="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="modal-input"
        />
        <input
          type="password"
          placeholder="New password (min 8 characters)"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          className="modal-input"
        />
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="modal-input"
        />
        {error && <p className="modal-error">{error}</p>}
        <div className="modal-actions">
          <button className="modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="modal-confirm"
            onClick={handleSubmit}
            disabled={!current || !newPwd || !confirm || submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Delete account modal
// =====================================================================
function DeleteAccountModal({ onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canDelete = password && confirmText === 'DELETE';

  const handleDelete = async () => {
    setError('');
    setSubmitting(true);
    try {
      await API.delete('/api/auth/account', {
        data: { password },
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2>Delete account?</h2>
        <p className="modal-warning">
          This is permanent. All your matches, messages, photos, and profile data
          will be erased. This cannot be undone.
        </p>
        <input
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="modal-input"
        />
        <input
          type="text"
          placeholder='Type DELETE to confirm'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="modal-input"
        />
        {error && <p className="modal-error">{error}</p>}
        <div className="modal-actions">
          <button className="modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="modal-danger"
            onClick={handleDelete}
            disabled={!canDelete || submitting}
          >
            {submitting ? 'Deleting...' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}

